import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useMessages } from '../../hooks/useMessages';
import { useMessageUrgency } from '../../hooks/useMessageUrgency';
import { usePermissions } from '../../contexts/PermissionContext';
import { useMessagesWebSocket } from '../../hooks/useMessagesWebSocket';
import { Message } from '../../types/messages';
import { UrgentMessageModal } from './UrgentMessageModal';
import { notify } from '../../lib/globalNotify';
import { playNotificationSound } from '../../lib/soundNotification';
import { sendToTerminal } from '../../lib/terminalLogger';

const LOG_PREFIX = '[MESSAGES_PROVIDER]';

/**
 * Provider que gerencia WebSocket (tempo real) e polling (fallback) para mensagens
 * Deve ser montado no App.tsx
 */
export function MessagesProvider() {
  const { messages, markAsRead, loadUnreadCount, refresh, isAdmin } = useMessages();
  const { shouldAutoShow } = useMessageUrgency();
  const { userInfo } = usePermissions(); // Verificar autenticação
  const [urgentMessage, setUrgentMessage] = useState<Message | null>(null);

  // Usar ref para processedMessages evitar re-renders desnecessários
  const processedMessagesRef = useRef<Set<number>>(new Set());
  // Flag para suprimir notificações no carregamento inicial (evitar som repetido após reload)
  const isInitialLoadRef = useRef(true);

  // Integração WebSocket com fallback para polling
  const { isConnected, useWebSocket } = useMessagesWebSocket(userInfo?.id, {
    // Nova mensagem recebida via WebSocket
    onNewMessage: useCallback((message: Message, _unreadCount: number | null) => {
      // Estado é atualizado automaticamente pelo invalidateQueries no hook
      // Aqui tratamos apenas notificações e sons

      // Processar para auto-exibição (se não lida)
      if (!message.lida) {
        // Marcar como processada para evitar duplicatas
        processedMessagesRef.current.add(message.id);
        const priority = message.prioridade || 'normal';

        // Tocar som para todas as mensagens não lidas
        playNotificationSound(priority);

        // Mensagens urgentes: modal bloqueante
        if (priority === 'urgente') {
          setUrgentMessage(message);
        }
        // Mensagens que devem ser exibidas automaticamente: toast sticky
        else if (shouldAutoShow(message)) {
          notify({
            type: message.tipo,
            title: message.titulo,
            message: message.mensagem,
            sticky: true,
            actions: [{
              label: 'Ver mensagem',
              onClick: () => {
                // O badge no footer abrirá o modal
              }
            }]
          });
        }
        // Todas as outras mensagens novas: toast não sticky (desaparece automaticamente)
        else {
          notify({
            type: message.tipo,
            title: 'Nova mensagem',
            message: message.titulo,
            sticky: false,
            duration: 5000, // 5 segundos
            actions: [{
              label: 'Ver',
              onClick: () => {
                // O badge no footer abrirá o modal
              }
            }]
          });
        }
      }
    }, [shouldAutoShow]),

    // Mensagem marcada como lida via WebSocket
    onMessageRead: useCallback((messageId: number, newUnreadCount: number) => {
      sendToTerminal(`✅ ${LOG_PREFIX} Mensagem lida via WebSocket`, {
        messageId,
        unreadCount: newUnreadCount
      });
      // Estado atualizado via React Query
      // Remover do set de processadas
      processedMessagesRef.current.delete(messageId);
    }, []),

    // Mensagem atualizada via WebSocket
    onMessageUpdated: useCallback((message: Message) => {
      sendToTerminal(`🔄 ${LOG_PREFIX} Mensagem atualizada via WebSocket`, {
        messageId: message.id,
        titulo: message.titulo
      });

      // Estado atualizado via React Query

      // Se foi atualizada para não lida, processar novamente
      if (!message.lida && !processedMessagesRef.current.has(message.id)) {
        processedMessagesRef.current.add(message.id);
        if (shouldAutoShow(message)) {
          const priority = message.prioridade || 'normal';
          playNotificationSound(priority);

          if (priority === 'urgente') {
            setUrgentMessage(message);
          }
        }
      }
    }, [shouldAutoShow]),

    // Mensagem deletada via WebSocket
    onMessageDeleted: useCallback((messageId: number) => {
      sendToTerminal(`🗑️ ${LOG_PREFIX} Mensagem deletada via WebSocket`, {
        messageId
      });
      // Estado atualizado via React Query
      processedMessagesRef.current.delete(messageId);
    }, []),

    // Resposta recebida (apenas admins)
    onReplyReceived: useCallback((event: any) => {
      if (!isAdmin) return;

      sendToTerminal(`💬 ${LOG_PREFIX} Resposta recebida via WebSocket`, {
        messageId: event.messageId,
        userId: event.userId,
        userName: event.userName
      });

      // Notificar admin sobre nova resposta
      notify({
        type: 'info',
        title: 'Nova resposta recebida',
        message: `${event.userName} respondeu a uma mensagem`,
        sticky: false
      });
    }, [isAdmin])
  });

  // Ref para controlar log de polling (evitar logs duplicados)
  const pollingListStartedRef = useRef(false);
  const pollingCountStartedRef = useRef(false);

  // Page Visibility API - pausar polling quando aba estiver oculta
  useEffect(() => {
    const handleVisibilityChange = () => {
      const isVisible = !document.hidden;
      sendToTerminal(`👁️ ${LOG_PREFIX} Visibilidade da página: ${isVisible ? 'visível' : 'oculta'}`);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Polling de contagem a cada 30s - APENAS COMO FALLBACK SE WebSocket NÃO ESTIVER DISPONÍVEL
  useEffect(() => {
    // Não iniciar polling sem autenticação
    if (!userInfo?.id) {
      return;
    }

    // Se WebSocket está conectado, não usar polling
    if (useWebSocket && isConnected) {
      if (pollingCountStartedRef.current) {
        pollingCountStartedRef.current = false;
      }
      return;
    }

    // Fallback: usar polling se WebSocket não estiver disponível
    if (!pollingCountStartedRef.current) {
      sendToTerminal(`🔄 ${LOG_PREFIX} Polling de contagem iniciado (fallback - 30s)`, {
        userId: userInfo.id,
        reason: 'WebSocket não disponível'
      });
      pollingCountStartedRef.current = true;
    }

    const interval = setInterval(() => {
      if (document.hidden) {
        return;
      }

      // Verificar novamente antes de cada chamada
      if (userInfo?.id && (!useWebSocket || !isConnected)) {
        loadUnreadCount();
      }
    }, 30000);

    return () => {
      clearInterval(interval);
      pollingCountStartedRef.current = false;
    };
  }, [loadUnreadCount, userInfo?.id, useWebSocket, isConnected]);

  // Polling de lista completa a cada 180s - APENAS COMO FALLBACK SE WebSocket NÃO ESTIVER DISPONÍVEL
  useEffect(() => {
    // Não iniciar polling sem autenticação
    if (!userInfo?.id) {
      return;
    }

    // Se WebSocket está conectado, não usar polling
    if (useWebSocket && isConnected) {
      if (pollingListStartedRef.current) {
        pollingListStartedRef.current = false;
      }
      return;
    }

    // Fallback: usar polling se WebSocket não estiver disponível
    if (!pollingListStartedRef.current) {
      sendToTerminal(`🔄 ${LOG_PREFIX} Polling de lista iniciado (fallback - 180s)`, {
        userId: userInfo.id,
        reason: 'WebSocket não disponível'
      });
      pollingListStartedRef.current = true;
    }

    const interval = setInterval(() => {
      if (document.hidden) {
        return;
      }

      // Verificar novamente antes de cada chamada
      if (userInfo?.id && (!useWebSocket || !isConnected)) {
        refresh();
      }
    }, 180000);

    return () => {
      clearInterval(interval);
      pollingListStartedRef.current = false;
    };
  }, [refresh, userInfo?.id, useWebSocket, isConnected]);

  // Limpar mensagens lidas do set de processadas
  useEffect(() => {
    // Remover todas as mensagens lidas do set de processadas
    if (messages.length > 0) {
      messages.forEach(message => {
        if (message.lida && processedMessagesRef.current.has(message.id)) {
          processedMessagesRef.current.delete(message.id);
        }
      });
    }
  }, [messages]);

  // Processar novas mensagens urgentes e notificações
  // No carregamento inicial, apenas registra como processadas (sem som/toast)
  // Após o initial load, mensagens novas disparam som e notificação normalmente
  useEffect(() => {
    if (messages.length === 0) return;

    // No primeiro carregamento, marcar todas as não-lidas como "já vistas" sem notificar
    if (isInitialLoadRef.current) {
      messages.forEach(message => {
        if (!message.lida) {
          processedMessagesRef.current.add(message.id);
        }
      });
      isInitialLoadRef.current = false;
      return;
    }

    messages.forEach(message => {
      // Se já foi lida ou já processada, ignorar
      if (message.lida || processedMessagesRef.current.has(message.id)) {
        return;
      }

      // Marcar como processada (usar ref para não causar re-render)
      processedMessagesRef.current.add(message.id);

      const priority = message.prioridade || 'normal';

      // Tocar som para mensagens novas (não no reload)
      playNotificationSound(priority);

      // Se é urgente, abrir modal bloqueante
      if (priority === 'urgente') {
        sendToTerminal(`🚨 ${LOG_PREFIX} Abrindo modal urgente`, { messageId: message.id });
        setUrgentMessage(message);
      }
      // Mensagens que devem ser exibidas automaticamente: toast sticky
      else if (shouldAutoShow(message)) {
        notify({
          type: message.tipo,
          title: message.titulo,
          message: message.mensagem,
          sticky: true,
          actions: [{
            label: 'Ver mensagem',
            onClick: () => {
              // O badge no footer abrirá o modal
            }
          }]
        });
      }
      // Todas as outras mensagens novas: toast não sticky
      else {
        notify({
          type: message.tipo,
          title: 'Nova mensagem',
          message: message.titulo,
          sticky: false,
          duration: 5000,
          actions: [{
            label: 'Ver',
            onClick: () => {
              // O badge no footer abrirá o modal
            }
          }]
        });
      }
    });
  }, [messages, shouldAutoShow]);

  const handleUrgentMessageClose = useCallback(async () => {
    if (urgentMessage) {
      sendToTerminal(`✅ ${LOG_PREFIX} Fechando modal urgente`, { messageId: urgentMessage.id });
      await markAsRead(urgentMessage.id);

      // Remover do set de processadas para evitar reprocessamento
      processedMessagesRef.current.delete(urgentMessage.id);

      setUrgentMessage(null);
    }
  }, [urgentMessage, markAsRead]);

  return (
    <>
      {urgentMessage && (
        <UrgentMessageModal
          message={urgentMessage}
          isOpen={true}
          onMarkAsRead={markAsRead}
          onClose={handleUrgentMessageClose}
        />
      )}
    </>
  );
}
